import { get } from './api';
import { API_ENDPOINTS } from '@utils/constants';

/**
 * Product Service - Enhanced
 * Handles all product-related API calls with proper error handling and data formatting
 */

/**
 * Format product response to ensure consistent data structure
 * @param {object} data - Raw API response
 * @returns {object} Formatted response
 */
const formatProductResponse = (data) => {
  // Handle different API response formats
  if (data.data && Array.isArray(data.data)) {
    return {
      products: data.data,
      data: data.data,
      total: data.total || data.data.length,
      page: data.page || 1,
      limit: data.limit || data.data.length,
      totalPages: data.totalPages || Math.ceil((data.total || data.data.length) / (data.limit || 10))
    };
  }

  if (Array.isArray(data)) {
    return {
      products: data,
      data: data,
      total: data.length,
      page: 1,
      limit: data.length,
      totalPages: 1
    };
  }

  return {
    products: data.products || [],
    data: data.products || data.data || [],
    total: data.total || 0,
    page: data.page || 1,
    limit: data.limit || 10,
    totalPages: data.totalPages || 1
  };
};

/**
 * Get all products with pagination and filters
 * @param {object} params - Query parameters
 * @param {number} params.page - Page number (default: 1)
 * @param {number} params.limit - Items per page (default: 12)
 * @param {string} params.category - Filter by category
 * @param {string} params.packaging - Filter by packaging size
 * @param {string} params.certification - Filter by certification
 * @param {string} params.sort - Sort order (featured, name_asc, name_desc, newest)
 * @returns {Promise} Products list with pagination info
 */
export const getProducts = async (params = {}) => {
  try {
    // Set defaults
    const queryParams = {
      page: params.page || 1,
      limit: params.limit || 12,
      ...params
    };

    const response = await get(API_ENDPOINTS.products, queryParams);
    return formatProductResponse(response.data || response);
  } catch (error) {
    console.error('Error fetching products:', error);
    
    // Return empty result instead of throwing to prevent UI crashes
    return {
      products: [],
      data: [],
      total: 0,
      page: 1,
      limit: 12,
      totalPages: 0,
      error: error.message || 'Failed to fetch products'
    };
  }
};

/**
 * Get single product by ID
 * @param {number|string} id - Product ID
 * @returns {Promise} Product details
 */
export const getProductById = async (id) => {
  try {
    if (!id) {
      throw new Error('Product ID is required');
    }

    const response = await get(API_ENDPOINTS.productDetail(id));
    return response.data || response;
  } catch (error) {
    console.error(`Error fetching product ${id}:`, error);
    throw error;
  }
};

/**
 * Get featured products
 * @param {number} limit - Number of products to fetch (default: 4)
 * @returns {Promise} Featured products
 */
export const getFeaturedProducts = async (limit = 4) => {
  try {
    const response = await get(API_ENDPOINTS.products, {
      featured: true,
      limit
    });
    
    return formatProductResponse(response);
  } catch (error) {
    console.error('Error fetching featured products:', error);
    
    return {
      products: [],
      data: [],
      total: 0,
      error: error.message || 'Failed to fetch featured products'
    };
  }
};

/**
 * Search products with query and filters
 * @param {string} query - Search query
 * @param {object} filters - Additional filters
 * @param {number} filters.page - Page number
 * @param {number} filters.limit - Items per page
 * @param {string} filters.category - Filter by category
 * @param {string} filters.packaging - Filter by packaging
 * @param {string} filters.sort - Sort order
 * @returns {Promise} Search results with pagination
 */
export const searchProducts = async (query, filters = {}) => {
  try {
    if (!query || query.trim() === '') {
      // If no query, return all products with filters
      return getProducts(filters);
    }

    const params = {
      search: query.trim(),
      page: filters.page || 1,
      limit: filters.limit || 12,
      ...filters
    };

    const response = await get(API_ENDPOINTS.products, params);
    return formatProductResponse(response);
  } catch (error) {
    console.error('Error searching products:', error);
    
    return {
      products: [],
      data: [],
      total: 0,
      page: 1,
      limit: 12,
      totalPages: 0,
      error: error.message || 'Search failed'
    };
  }
};

/**
 * Get products by category
 * @param {string} category - Category name or ID
 * @param {object} params - Additional parameters
 * @returns {Promise} Products in category
 */
export const getProductsByCategory = async (category, params = {}) => {
  try {
    if (!category) {
      throw new Error('Category is required');
    }

    const queryParams = {
      category,
      page: params.page || 1,
      limit: params.limit || 12,
      ...params
    };

    const response = await get(API_ENDPOINTS.products, queryParams);
    return formatProductResponse(response);
  } catch (error) {
    console.error(`Error fetching products for category ${category}:`, error);
    
    return {
      products: [],
      data: [],
      total: 0,
      error: error.message || 'Failed to fetch category products'
    };
  }
};

/**
 * Get related products for a specific product
 * @param {number|string} productId - Product ID
 * @param {number} limit - Number of related products (default: 4)
 * @returns {Promise} Related products
 */
export const getRelatedProducts = async (productId, limit = 4) => {
  try {
    if (!productId) {
      throw new Error('Product ID is required');
    }

    const response = await get(`${API_ENDPOINTS.products}/${productId}/related`, {
      limit
    });
    
    return formatProductResponse(response);
  } catch (error) {
    console.error(`Error fetching related products for ${productId}:`, error);
    
    return {
      products: [],
      data: [],
      total: 0,
      error: error.message || 'Failed to fetch related products'
    };
  }
};

/**
 * Get product availability/stock status
 * @param {number|string} productId - Product ID
 * @returns {Promise} Availability info
 */
export const getProductAvailability = async (productId) => {
  try {
    if (!productId) {
      throw new Error('Product ID is required');
    }

    const response = await get(`${API_ENDPOINTS.products}/${productId}/availability`);
    return response.data || response;
  } catch (error) {
    console.error(`Error fetching availability for product ${productId}:`, error);
    throw error;
  }
};

export default {
  getProducts,
  getProductById,
  getFeaturedProducts,
  searchProducts,
  getProductsByCategory,
  getRelatedProducts,
  getProductAvailability,
};